import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangleIcon, AmbulanceIcon, BedDoubleIcon, Clock3Icon, HospitalIcon, UsersRoundIcon } from 'lucide-react';
import { EocAnalytics } from '../components/emergency/EocAnalytics';
import { EocCommandHeader } from '../components/emergency/EocCommandHeader';
import { EocCommandRail } from '../components/emergency/EocCommandRail';
import { EocWhiteboard } from '../components/emergency/EocWhiteboard';
import { Card } from '../components/ui/Card';
import { eocKpis, eocPatients, type EocKpiTone, type EocPatient } from '../data/emergencyCommand';
import { cn } from '../lib/cn';
type CommandFilter = 'All patients' | 'High acuity' | 'Waiting' | 'Boarding';
const kpiStyle: Record<KpiTone, {
  icon: React.ElementType;
  accent: string;
  value: string;
}> = {
  blue: {
    icon: UsersRoundIcon,
    accent: 'bg-blue-50 text-[#0057D9]',
    value: 'text-slate-900'
  },
  red: {
    icon: AlertTriangleIcon,
    accent: 'bg-red-50 text-red-700',
    value: 'text-red-700'
  },
  teal: {
    icon: Clock3Icon,
    accent: 'bg-teal-50 text-teal-700',
    value: 'text-slate-900'
  },
  amber: {
    icon: BedDoubleIcon,
    accent: 'bg-amber-50 text-amber-700',
    value: 'text-amber-700'
  },
  sky: {
    icon: AmbulanceIcon,
    accent: 'bg-sky-50 text-sky-700',
    value: 'text-slate-900'
  }
};
export function EmergencyBoard() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CommandFilter>('All patients');
  const [selectedId, setSelectedId] = useState(eocPatients[0].id);
  const [notice, setNotice] = useState<string | null>(null);
  const patients = useMemo(() => {
    if (filter === 'High acuity') return eocPatients.filter(patient => patient.triage === 'ESI-1' || patient.triage === 'ESI-2' || patient.deteriorationRisk === 'High');
    if (filter === 'Waiting') return eocPatients.filter(patient => patient.status === 'Triage' || !patient.provider);
    if (filter === 'Boarding') return eocPatients.filter(patient => patient.status === 'Admit pending' || patient.disposition.includes('pending'));
    return eocPatients;
  }, [filter]);
  const selectedPatient = eocPatients.find(patient => patient.id === selectedId) || eocPatients[0];
  const selectPatient = (patient: EocPatient) => {
    setSelectedId(patient.id);
  };
  const runQuickAction = (action: string) => {
    setNotice(`${action} command staged for the emergency coordination team.`);
  };
  return <div className="min-h-full w-full bg-[#F4F6F9] p-3 sm:p-5 xl:p-6" data-id="element-1527" data-mp="7oagkswz2kd8">
      <div className="mx-auto max-w-[1900px]" data-id="element-1528" data-mp="cienjc68mseb">
        <EocCommandHeader onQuickAction={runQuickAction} onNewArrival={() => navigate('/reception/encounter')} data-id="element-1529" data-mp="3txlg8rg2v6js" />

        <AnimatePresence data-id="element-1530" data-mp="2qh97wjxnkbd">
          {notice && <motion.div initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-id="element-1531" data-mp="2oxuwbhu7jc8">
              <HospitalIcon className="h-4 w-4 text-[#0057D9]" data-id="element-1532" data-mp="369exiyd4gbkh" />
              <span className="font-semibold" data-id="element-1533" data-mp="36brkjpxqlc4e">Command acknowledged.</span>
              <span className="text-blue-700" data-id="element-1534" data-mp="31cergng9v8td">{notice}</span>
              <button type="button" onClick={() => setNotice(null)} className="ml-auto text-xs font-bold text-[#0057D9] hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0057D9]" data-id="element-1535" data-mp="3fav6d1oag5ml">Dismiss</button>
            </motion.div>}
        </AnimatePresence>

        <section className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-6" aria-label="Emergency command key performance indicators" data-id="element-1536" data-mp="3ka7stlb8zaon">
          {eocKpis.map((kpi, index) => <EocKpi key={kpi.label} index={index} {...kpi} data-id="element-1537" data-mp="3tziwgsfd7n55" data-mpi={index} />)}
        </section>

        <div className="mt-4 grid gap-4 2xl:grid-cols-[minmax(0,1fr)_340px]" data-id="element-1538" data-mp="3u1lwm5a8ewnl">
          <main className="min-w-0 space-y-4" data-id="element-1539" data-mp="bufmouegemye">
            <EocWhiteboard patients={patients} filter={filter} onFilterChange={setFilter} selectedId={selectedId} onSelect={selectPatient} data-id="element-1540" data-mp="6v34imyj2425" />
            <EocAnalytics data-id="element-1541" data-mp="21a8jgqowvgef" />
          </main>
          <EocCommandRail selectedPatient={selectedPatient} data-id="element-1542" data-mp="26a4n2otp55tg" />
        </div>
      </div>
    </div>;
}
function EocKpi({
  label,
  value,
  detail,
  trend,
  tone,
  index
}: {
  label: string;
  value: string;
  detail: string;
  trend: string;
  tone: EocKpiTone;
  index: number;
}) {
  const style = kpiStyle[tone];
  const Icon = style.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 7
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.035
  }} data-id="element-1543" data-mp="1icp9nkumptqy">
    <Card className="h-full p-3.5 sm:p-4" data-id="element-1544" data-mp="1nca3xhyutdxc"><div className="flex items-start justify-between gap-3" data-id="element-1545" data-mp="1ndcvzt7wxmeu"><span className={cn('flex h-8 w-8 items-center justify-center rounded-lg', style.accent)} data-id="element-1546" data-mp="1rckjnmcg7ipe"><Icon className="h-4 w-4" data-id="element-1547" data-mp="1wcgi762dmhxk" /></span><span className="text-right text-[10px] font-semibold leading-tight text-slate-400" data-id="element-1548" data-mp="1wdi2801deerf">{trend}</span></div><p className={cn('mt-3 font-display text-2xl font-bold tracking-tight tabular-nums', style.value)} data-id="element-1549" data-mp="32rhgvjisde7u">{value}</p><p className="mt-0.5 text-xs font-bold text-slate-700" data-id="element-1550" data-mp="37p97ma7p2uag">{label}</p><p className="mt-1 text-[10px] text-slate-400" data-id="element-1551" data-mp="3htzcanuhgcid">{detail}</p></Card>
  </motion.div>;
}